import IdleJs from 'idle-js'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat';
import 'dayjs/locale/fr'
import currencyCode from 'currency-codes'
import $ from 'jquery'
import router from './router.js'
import i18n from './i18n.js'
import { vueKeycloak } from '@josempgon/vue-keycloak'
import { createApp } from 'vue'
import { ToastPlugin, useToast } from 'vue-toast-notification'
import 'vue-toast-notification/dist/theme-bootstrap.css'

import App from './App.vue'
import store from './store.js'

// Bootstrap
import 'bootstrap/dist/css/bootstrap.min.css'
// main.js or app.js

const app = createApp(App)

const savedLocale = localStorage.getItem('user-locale');
if (savedLocale) {
  i18n.global.locale = savedLocale; // Set the saved locale
}

app.use(store)
  .use(router)
  .use(i18n)
  .use(ToastPlugin, {
    position: 'bottom'
  })

initKeycloak()

let warningTimeout;
let warningShown = false;

var idle = new IdleJs({
  idle: 840000, // idle time in ms
  events: ['mousemove', 'keydown', 'mousedown', 'touchstart'], // events that will trigger the idle resetter
  onIdle: () => {
    if (!warningShown) {
      showWarningBeforeLogout();
    }
  },
  keepTracking: true, // set it to false if you want to be notified only on the first idleness change
  startAtIdle: false // set it to true if you want to start in the idle state
})

idle.start()

function showWarningBeforeLogout() {
  warningShown = true // Set the flag indicating the warning has been shown

  // Using i18n for translation
  const warningMessage = i18n.global.t('label.logout.warning')

  // Show the warning message as a toast notification
  const $toast = useToast()
  $toast.warning(warningMessage, {
    title: "Warning",
    duration: 60000, // Duration for the warning toast
    position: "bottom", // Position on the screen
    onClose: () => {
      stayLoggedIn(); // Call the function to stay logged in
    },
    // Add action buttons for staying logged in
    onClick: () => {
      stayLoggedIn(); // Call the function to stay logged in
    },
    onDismiss: () => {
      stayLoggedIn(); // Call the function to stay logged in
    }
  });

  // Set timeout for logout
  warningTimeout = setTimeout(() => {
    logoutIdle() // Call logout after 60 seconds
  }, 60000); // 60 seconds for warning timeout
}

function stayLoggedIn() {
  clearTimeout(warningTimeout) // Clear the logout timeout
  warningShown = false // Reset flag to show warning again if idle
}

function logoutIdle() {
  if (store.state.auth.status.loggedIn) {
    store.dispatch('auth/logout').then(
      () => {
        const keycloak = app.config.globalProperties.$keycloak
        if (keycloak) {
          const disconnectedMessage = i18n.global.t('label.logout.disconnected')
          const $toast = useToast()
          $toast.info(disconnectedMessage, {
            title: "Disconnected",
            duration: 10000, // Duration for the warning toast
            position: "bottom", // Position on the screen
          });
          // Proceed to logout from Keycloak
          router.replace({ path: '/home' })
          store.dispatch('auth/logout').then(
          () => {
            keycloak.logout()
          }
    )
        }
      }
    )
    .catch(error => {
      console.error("Error during logout action:", error)
    });
  }
}

dayjs.extend(customParseFormat)
app.config.globalProperties.$dayjs = dayjs;

app.config.globalProperties.$filters = {
  uppercase (value) {
    return value.toUpperCase()
  },

  boolean (value) {
    return value ? 'O' : 'N'
  },

  date (value) {
    return value ? dayjs(value).format('YYYY/MM/DD HH:mm:ss') : value
  },

  timestamp (value) {
    return value ? dayjs.unix(value).format('YYYY/MM/DD HH:mm:ss') : value
  },

  dateTransmission (value) {
    return value ? dayjs(value, 'MMDDHHmmss').format("'MM/DD HH:mm:ss'") : value
  },

  dateYYYYMMDD (value) {
    return value ? dayjs(value).format('YYYY/MM/DD') : value
  },

  dateYYYYMMDDTirets (value) {
    return value ? dayjs(value).format('YYYY-MM-DD') : value
  },

  dateYYYYMMDDTiretsOrNull (value) {
    return value ? dayjs(value).format('YYYY-MM-DD') : '--'
  },

  amount (value) {
    //On check la longueur de value pour ne pas tenir compte de la virgule (presente dans la data amountLongFormat)
    let amountToParse = value && value.length > 10
    let amountLongFormat = value ? new Intl.NumberFormat('fr-FR').format(value / 100) : value
    if (amountLongFormat && amountToParse) {
      //Si presence de gros montants, on tronque à 10 dans les exports (on affiche donc les 2 montants sur l'IHM)
      amountLongFormat += ' (montant présent dans les exports : ' +
      new Intl.NumberFormat('fr-FR').format(value.toString().substring(0, 10) / 100)
      + ')'
    }
    return amountLongFormat

  },

  amountImpact (value, currency) {
    return value && currency && currencyCode.number(currency) ? new Intl.NumberFormat('fr-FR', { style: 'currency', currency: currencyCode.number(currency).code }).format(value / 100) : value
  },

  currency (value, currency) {
    //On check la longueur de value pour ne pas tenir compte de la virgule (presente dans la data amountLongFormat)
    let amountToParse = value && value.length > 10
    let amountLongFormat = value && currency && currencyCode.number(currency) ? new Intl.NumberFormat('fr-FR', { style: 'currency', currency: currencyCode.number(currency).code }).format(value / 100) : value
    if (amountLongFormat && amountToParse) {
      //Si presence de gros montants, on tronque à 10 dans les exports (on affiche donc les 2 montants sur l'IHM)
      amountLongFormat += ' (montant présent dans les exports : ' +
      (value && currency && currencyCode.number(currency) ? new Intl.NumberFormat('fr-FR', { style: 'currency', currency: currencyCode.number(currency).code }).format(value.toString().substring(0, 10) / 100) : value)
      + ')'
    }
    return amountLongFormat
  },

  amountObject (value) {
    //On check la longueur de value pour ne pas tenir compte de la virgule (presente dans la data amountLongFormat)
    let amountToParse = value && value.length > 10
    let amountLongFormat = value && value.amount && value.currency && currencyCode.number(value.currency) && value.exponent ? new Intl.NumberFormat('fr-FR', { style: 'currency', currency: currencyCode.number(value.currency).code }).format(value.amount / Math.pow(10, value.exponent)) : value && value.amount ? value.amount : ''
    if (amountLongFormat && amountToParse) {
      //Si presence de gros montants, on tronque à 10 dans les exports (on affiche donc les 2 montants sur l'IHM)
      amountLongFormat += ' (montant présent dans les exports : ' +
      (value && value.amount && value.currency && currencyCode.number(value.currency) && value.exponent ? new Intl.NumberFormat('fr-FR', { style: 'currency', currency: currencyCode.number(value.currency).code }).format(value.amount.toString().substring(0, 10) / Math.pow(10, value.exponent)) : '')
      + ')'
    }
    return amountLongFormat
  },

  conversionRate (value, exponent) {
    return value && exponent ? value / Math.pow(10, exponent) : value
  },

  nullIsZero (value) {
    return value || value !== '' ? value : '0'
  },

  nullFilter (value) {
    return value === null ? '--' : value
  }
}

app.directive('tooltip', function (el) {
  $(el).tooltip()
})

async function initKeycloak() {
  await vueKeycloak.install(app, {
    config: {
      url: process.env.VUE_APP_KEYCLOAK_URL,
      realm: process.env.VUE_APP_KEYCLOAK_REALM,
      clientId: process.env.VUE_APP_KEYCLOAK_CLIENT_ID,
    },
    loadUserProfileAtStartUp: true,
    initOptions: {
      onLoad: 'login-required',
      messageReceiveTimeout:30000,
      checkLoginIframe: false,
    },
    // Enables Bearer interceptor
    enableBearerInterceptor: true,
    // Prefix for the Bearer token
    bearerPrefix: 'Bearer',
  }).then(() => {
    const keycloak = app.config.globalProperties.$keycloak
    if (keycloak) {
      // Get the locale from Keycloak (check if the preferred_locale exists or fallback)
      const locale = keycloak.idTokenParsed.locale || 'fr';
      // Set the i18n locale based on Keycloak's locale value
      i18n.global.locale = locale;
    } else {
      i18n.locale = process.env.VUE_APP_LOCALE
      console.error('Authentication failed');
    }
  })
  .catch((error) => {
    i18n.locale = process.env.VUE_APP_LOCALE
    console.error('Keycloak object in error : ', app.config.globalProperties.$keycloak);
    console.error('Keycloak init error:', error);
  });

  app.mount('#app')
}
